/* eslint-disable */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import EventCard from "../components/EventCard";
import api from "../lib/api";

// Session 55.ad · Date chips replace a heavy date-picker — 90% of the time
// users just want "this week" or "this month".
const DATE_CHIPS = [
  { key: "",   label: "Any time",  days: 0  },
  { key: "7",  label: "This week", days: 7  },
  { key: "30", label: "This month", days: 30 },
  { key: "90", label: "Next 90 days", days: 90 },
];

export default function LocalEvents() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [events, setEvents] = useState([]);
  // Session 60.27 · Past Events section (backend endpoint filters
  // status='archived' OR published-but-elapsed, newest-completed first).
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const cityQ = params.get("city") || "";
  const catQ  = params.get("category") || "";
  const daysQ = params.get("within_days") || "";
  const costQ = params.get("cost") || "";
  const modQ  = params.get("modality") || "";

  useEffect(() => {
    api.get("/events/categories").then(r => setCategories(r.data || []));
    // Session 55.ad · Dynamic city list from published events.
    api.get("/events/cities").then(r => setCities(r.data || [])).catch(() => setCities([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (cityQ) q.city = cityQ;
    if (catQ)  q.category = catQ;
    if (daysQ) q.within_days = daysQ;
    if (costQ) q.cost = costQ;
    if (modQ)  q.modality = modQ;
    Promise.all([
      api.get("/events/local", { params: q }).then(r => r.data || []).catch(() => []),
      // Session 60.27 · Past events run alongside — separate endpoint,
      // separate section, same event data model (no duplicate records).
      api.get("/events/past", { params: { limit: 24 } }).then(r => r.data || []).catch(() => []),
    ])
      .then(([up, past]) => { setEvents(up); setPastEvents(past); })
      .finally(() => setLoading(false));
  }, [cityQ, catQ, daysQ, costQ, modQ]);

  const setCity = (v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "All cities") next.delete("city"); else next.set("city", v);
    setParams(next);
  };
  const setCat = (v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete("category"); else next.set("category", v);
    setParams(next);
  };
  const setDays = (v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete("within_days"); else next.set("within_days", v);
    setParams(next);
  };
  const setCost = (v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete("cost"); else next.set("cost", v);
    setParams(next);
  };
  const setMod = (v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete("modality"); else next.set("modality", v);
    setParams(next);
  };
  // Session 60.26 · Backend already filters past events out and sorts
  // `start_at` ascending — see server.py `/api/events/local`. Keep an
  // explicit client-side chronological sort as a defence-in-depth guard
  // so a future API tweak can never break the "nearest date first" order.
  const chronological = [...events].sort((a, b) => {
    const ax = String(a?.start_at || "");
    const bx = String(b?.start_at || "");
    if (ax === bx) return 0;
    return ax < bx ? -1 : 1;
  });
  const featured = chronological.filter(e => e.featured);
  const upcoming = chronological.filter(e => !e.featured);

  return (
    <AppShell>
      <div className="m-page-pad" data-testid="local-events-page">
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16, flexWrap:"wrap"}}>
          <div>
            <span className="m-eyebrow">Discover</span>
            <h1 className="m-h1">Local <em>events</em></h1>
            <p className="m-muted" style={{maxWidth: 620, marginTop: 4}}>
              Professional events, networking, and startup gatherings across the Métier network. Métier doesn&apos;t run these, every event links straight to the organizer.
            </p>
          </div>
          {/* Session 60.6 · Members can now submit events for admin review. */}
          <Link
            to="/events/submit"
            className="m-btn-gold"
            data-testid="events-submit-cta"
            style={{whiteSpace:"nowrap", alignSelf:"flex-start", marginTop:8}}
          >
            Submit an event &rarr;
          </Link>
        </div>

        <div className="m-events-filter" data-testid="events-filter">
          <div className="m-events-filter-group">
            <label className="m-events-filter-label">Location</label>
            <select
              value={cityQ || "All cities"}
              onChange={(e) => setCity(e.target.value)}
              className="m-events-filter-select"
              data-testid="events-filter-city"
            >
              <option value="">All cities</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="m-events-filter-group">
            <label className="m-events-filter-label">When</label>
            <div className="m-events-cat-chips" role="tablist">
              {DATE_CHIPS.map(chip => (
                <button
                  key={chip.key || "any"}
                  type="button"
                  className={`m-events-cat-chip ${daysQ === chip.key ? "on" : ""}`}
                  onClick={() => setDays(chip.key)}
                  data-testid={`events-when-${chip.key || "any"}`}
                >{chip.label}</button>
              ))}
            </div>
          </div>
          <div className="m-events-filter-group">
            <label className="m-events-filter-label">Category</label>
            <div className="m-events-cat-chips" role="tablist">
              <button
                type="button"
                className={`m-events-cat-chip ${!catQ ? "on" : ""}`}
                onClick={() => setCat("all")}
                data-testid="events-cat-all"
              >All</button>
              {categories.map(c => (
                <button
                  key={c.key}
                  type="button"
                  className={`m-events-cat-chip ${catQ === c.key ? "on" : ""}`}
                  onClick={() => setCat(c.key)}
                  data-testid={`events-cat-${c.key}`}
                >{c.label}</button>
              ))}
            </div>
          </div>
          <div className="m-events-filter-group">
            <label className="m-events-filter-label">Cost</label>
            <div className="m-events-cat-chips" role="tablist">
              {[{k:"",l:"Any"},{k:"free",l:"Free"},{k:"paid",l:"Paid"}].map(c => (
                <button
                  key={c.k || "cost-any"}
                  type="button"
                  className={`m-events-cat-chip ${costQ === c.k ? "on" : ""}`}
                  onClick={() => setCost(c.k)}
                  data-testid={`events-cost-${c.k || "any"}`}
                >{c.l}</button>
              ))}
            </div>
          </div>
          <div className="m-events-filter-group">
            <label className="m-events-filter-label">Format</label>
            <div className="m-events-cat-chips" role="tablist">
              {[{k:"",l:"Any"},{k:"in_person",l:"In person"},{k:"virtual",l:"Virtual"},{k:"hybrid",l:"Hybrid"}].map(m => (
                <button
                  key={m.k || "mod-any"}
                  type="button"
                  className={`m-events-cat-chip ${modQ === m.k ? "on" : ""}`}
                  onClick={() => setMod(m.k)}
                  data-testid={`events-modality-${m.k || "any"}`}
                >{m.l}</button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="m-loading-inline">Loading events…</div>
        ) : (
          <>
            {featured.length > 0 && (
              <section className="m-events-section" data-testid="events-featured">
                <span className="m-eyebrow" style={{color:"var(--gold)"}}>Featured</span>
                <div className="m-events-grid">
                  {featured.map(e => <EventCard key={e.event_id} ev={e} />)}
                </div>
              </section>
            )}

            {/* Session 60.26 · Explicit "Upcoming Events" section — always
                rendered, even when zero results, so the section serves as a
                reliable landmark on the Events page. */}
            <section className="m-events-section" data-testid="events-upcoming">
              <h2 className="m-h2" style={{ margin: "6px 0 4px" }}>
                {upcoming.length > 0
                  ? `${upcoming.length} event${upcoming.length === 1 ? "" : "s"} coming up`
                  : "Upcoming Events"}
              </h2>
              <p className="m-muted" style={{ maxWidth: 620, marginTop: 0, marginBottom: 14 }}>
                Discover professional events happening near you.
              </p>
              {upcoming.length === 0 ? (
                <div className="m-events-empty" data-testid="events-empty">
                  <h3 className="rec-h2">No upcoming events matched that filter.</h3>
                  <p className="m-muted">
                    Try a different location, category, or date — or{" "}
                    <Link to="/events" style={{color:"var(--gold)"}}>reset filters</Link>.
                    New events land here as organizers publish them.
                  </p>
                </div>
              ) : (
                <div className="m-events-grid">
                  {upcoming.map(e => <EventCard key={e.event_id} ev={e} />)}
                </div>
              )}
            </section>

            {/* Session 60.27 · Past Events — archived / completed events
                sorted newest-first. Reuses EventCard with isPast={true}
                so the badge + CTA copy reflect the completed status. */}
            {pastEvents.length > 0 && (
              <section className="m-events-section" data-testid="events-past">
                <h2 className="m-h2" style={{ margin: "6px 0 4px" }}>Past Events</h2>
                <p className="m-muted" style={{ maxWidth: 620, marginTop: 0, marginBottom: 14 }}>
                  A record of events Métier has hosted and discovered previously.
                </p>
                <div className="m-events-grid">
                  {pastEvents.map(e => <EventCard key={e.event_id} ev={e} isPast />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
